import { useState, useEffect, useRef } from 'react';
import { sortByName } from '../../../../utils/sortingUtils';

/**
 * AddFeatureControl - Dropdown control for including existing features in the rule table
 * @param {Object} props - Component props
 * @param {Array} props.featuresMetadata - Array of all feature metadata
 * @param {Array} props.currentHeaders - Currently selected headers in the rule table
 * @param {Function} props.onAdd - Callback when a feature is included
 * @returns {JSX.Element} - Rendered include feature control
 */
export default function AddFeatureControl({ featuresMetadata, currentHeaders, onAdd }) {
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    const filteredOptions = sortByName(
        featuresMetadata
            .filter(v => !currentHeaders.includes(v.name))
            .filter(v => v.name.toLowerCase().includes(search.toLowerCase())),
        'name'
    );

    const isNewName = search.trim() && !featuresMetadata.some(v => v.name.toLowerCase() === search.trim().toLowerCase());

    const handleSelect = (name) => {
        onAdd(name);
        setSearch('');
        setIsOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => setIsOpen(!isOpen)} 
                style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px', padding: '0.5rem 1rem' }}
            >
                <i className="fa-solid fa-plus"></i> Include feature
            </button>
            {isOpen && (
                <div className="select-dropdown fade-in" style={{ position: 'absolute', top: '100%', right: 0, background: 'var(--bg-primary)', border: '1px solid var(--border)', zIndex: 100, minWidth: '220px', borderRadius: 'var(--radius)', padding: '0.75rem', marginTop: '0.5rem', boxShadow: '0 4px 20px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <input
                        className="input-field"
                        placeholder="Search or type new name..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ width: '100%', padding: '0.5rem' }}
                        autoFocus
                    />
                    <div style={{ maxHeight: '150px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {filteredOptions.map(opt => (
                            <div
                                key={opt.id}
                                onClick={() => handleSelect(opt.name)}
                                style={{ padding: '6px 8px', cursor: 'pointer', borderRadius: '4px', fontSize: '0.9rem' }}
                                onMouseEnter={e => e.target.style.background = 'var(--bg-secondary)'}
                                onMouseLeave={e => e.target.style.background = 'transparent'}
                            >
                                {opt.name}
                            </div>
                        ))}
                        {isNewName && (
                            <div
                                onClick={() => handleSelect(search.trim())}
                                style={{ padding: '6px 8px', cursor: 'pointer', color: 'var(--accent)', fontWeight: 'bold', borderRadius: '4px', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                                onMouseEnter={e => e.target.style.background = 'var(--bg-secondary)'}
                                onMouseLeave={e => e.target.style.background = 'transparent'}
                            >
                                <i className="fa-solid fa-wand-magic-sparkles"></i> Create new: "{search.trim()}"
                            </div>
                        )}
                        {filteredOptions.length === 0 && !isNewName && (
                            <div style={{ padding: '6px 8px', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                                No features match search
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
